import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import {
  Card,
  CardBody,
  CardFooter,
  CardTitle,
  Modal,
  Table,
} from "reactstrap"
import { MdModeEdit } from "react-icons/md"
import {
  InputField,
  InputNumber,
  SelectFieldForId,
  TextareaField,
} from "components/Common/input-fields"
import Loader from "components/Common/loader"
import Notify from "components/Common/notify"
import { paymentFetchAPI } from "api/payment/paymentApiHelper"
import { scholarshipByStudentIdAPI } from "api/scholarship/scholarshipApiHelper"
import {
  ScholarshipCreate,
  ScholarshipUpdate,
} from "redux/slice/scholarship/scholarshipSlice"
import { PaymentUpdate } from "redux/slice/payment/paymentSlice"
import { CURRENCY_OPTIONS } from "common/data/scholarshipOptions"
import {
  PAYMENT_MODE_OPTIONS,
  IS_PAID_OPTIONS,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
} from "common/data/paymentOptions"

// Robustly find the list in the (possibly nested) API response body.
const extractList = root => {
  const visit = node => {
    if (!node || typeof node !== "object") return null
    if (Array.isArray(node)) return node
    if (Array.isArray(node.items)) return node.items
    for (const key of ["data", "items", "result", "payload", "records"]) {
      if (node[key]) {
        const found = visit(node[key])
        if (found) return found
      }
    }
    return null
  }
  return visit(root) || []
}

// All 11 scholarship fields (mirrors the Scholarship module's createData).
const schInitial = ({ applicationId, studentName, country, courseDuration }) => ({
  paymentId: "",
  partnerId: "",
  applicationId:
    applicationId === null || applicationId === undefined ? "" : applicationId,
  country: country || "",
  studentName: studentName || "",
  courseDuration: courseDuration || "",
  semestersPerYear: "",
  annualScholarshipCommitted: "",
  currency: "",
  firstPaymentDueDate: "",
  notes: "",
})

// Map a payment row into the edit-form shape (mirrors pickPaymentData).
const pickPayment = r => ({
  id: r?.id,
  scholarshipFinanceDataId: r?.scholarshipFinanceDataId || "",
  currentYear: r?.currentYear || "",
  currentSemester: r?.currentSemester || "",
  isPaid: r?.isPaid ? r.isPaid : false,
  amountPaid: r?.amountPaid ? r.amountPaid : "",
  paymentModeEnum:
    r?.paymentModeEnum === null || r?.paymentModeEnum === undefined
      ? ""
      : r.paymentModeEnum,
  firstPaymentDueDate: r?.firstPaymentDueDate
    ? r.firstPaymentDueDate.slice(0, 10)
    : "",
  paymentMadeDate: r?.paymentMadeDate ? r.paymentMadeDate.slice(0, 10) : "",
  year: r?.year ? r.year : "",
  month: r?.month ? r.month : "",
  challanNumber: r?.challanNumber ? r.challanNumber : "",
})

export default function ApplicationScholarshipSection({
  applicationId,
  studentId,
  studentName,
  country,
  courseDuration,
  applicationStatus,
  isViewer,
}) {
  const dispatch = useDispatch()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  // Scholarship create/update modal (one modal, two modes).
  const [schModal, setSchModal] = useState(false)
  const [schMode, setSchMode] = useState("create") // "create" | "update"
  const [schId, setSchId] = useState(null)
  const [schData, setSchData] = useState(
    schInitial({ applicationId, studentName, country, courseDuration }),
  )
  // When the application has no course duration, let the user enter it here.
  const [needCourseDuration, setNeedCourseDuration] = useState(false)

  // Payment edit modal.
  const [editModal, setEditModal] = useState(false)
  const [editData, setEditData] = useState(null)

  const hasAppId =
    applicationId !== "" && applicationId !== null && applicationId !== undefined

  // A scholarship can only be created for Accepted or Returning applications.
  const canCreate = ["accepted", "returning"].includes(
    (applicationStatus || "").toLowerCase(),
  )

  const fetchPayments = async () => {
    if (!hasAppId || !canCreate) return
    setLoading(true)
    try {
      const res = await paymentFetchAPI({
        pageNumber: 1,
        pageSize: 100,
        ApplicationId: applicationId,
        asce: false,
      })
      setRows(extractList(res?.data))
    } catch (e) {
      console.log("error fetching scholarship payments", e)
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPayments()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, applicationStatus])

  // ---- Scholarship create/update ----
  const handleSchChange = (field, value) =>
    setSchData(prev => ({ ...prev, [field]: value }))

  const openCreate = () => {
    setSchData(schInitial({ applicationId, studentName, country, courseDuration }))
    setNeedCourseDuration(!courseDuration)
    setSchMode("create")
    setSchId(null)
    setSchModal(true)
  }

  const openScholarshipEdit = async () => {
    if (!studentId) {
      Notify("error", "Application is still loading. Please try again.")
      return
    }
    let record
    try {
      const resp = await scholarshipByStudentIdAPI(studentId)
      const body = resp?.data
      record =
        body?.items?.data ?? body?.data ?? body?.items ?? body
      if (Array.isArray(record)) record = record[0]
    } catch (e) {
      console.log("error loading scholarship by student id", e)
    }
    if (!record || !record.id) {
      Notify("error", "Could not load the scholarship details.")
      return
    }
    setSchData({
      paymentId: record.paymentId || "",
      partnerId: record.partnerId || "",
      applicationId:
        record.applicationId === null || record.applicationId === undefined
          ? ""
          : record.applicationId,
      country: record.country || "",
      studentName: record.studentName || "",
      courseDuration: record.courseDuration || "",
      semestersPerYear: record.semestersPerYear || "",
      annualScholarshipCommitted: record.annualScholarshipCommitted || "",
      currency: record.currency || "",
      firstPaymentDueDate: record.firstPaymentDueDate
        ? record.firstPaymentDueDate.slice(0, 10)
        : record?.payments?.[0]?.firstPaymentDueDate
        ? record.payments[0].firstPaymentDueDate.slice(0, 10)
        : "",
      notes: record.notes || "",
    })
    setNeedCourseDuration(!record.courseDuration)
    setSchId(record.id)
    setSchMode("update")
    setSchModal(true)
  }

  const handleSchSubmit = async () => {
    if (!hasAppId && schMode === "create") {
      Notify("error", "Application is still loading. Please try again.")
      return
    }
    const missing = []
    if (!schData.partnerId) missing.push("Partner ID")
    if (needCourseDuration && !schData.courseDuration)
      missing.push("Course Duration")
    if (!schData.semestersPerYear) missing.push("Semesters Per Year")
    if (!schData.annualScholarshipCommitted)
      missing.push("Annual Scholarship Committed")
    if (!schData.currency) missing.push("Currency")
    if (!schData.firstPaymentDueDate) missing.push("First Payment Due Date")
    if (missing.length) {
      Notify("error", `Please fill the required field(s): ${missing.join(", ")}`)
      return
    }

    // Exact same payload shape as the Scholarship module.
    const payload = {
      ...schData,
      country: schData.country || null,
      courseDuration: schData.courseDuration
        ? String(schData.courseDuration)
        : null,
      semestersPerYear: schData.semestersPerYear || null,
      firstPaymentDueDate: schData.firstPaymentDueDate
        ? new Date(schData.firstPaymentDueDate).toISOString()
        : null,
      notes: schData.notes || null,
    }

    setSubmitting(true)
    if (schMode === "update") {
      const resp = await dispatch(ScholarshipUpdate({ id: schId, data: payload }))
      setSubmitting(false)
      if (resp?.payload?.status === 200) {
        Notify("success", "Scholarship Updated")
        setSchModal(false)
        fetchPayments()
      }
    } else {
      const resp = await dispatch(ScholarshipCreate(payload))
      setSubmitting(false)
      if (resp?.payload?.status === 201 || resp?.payload?.status === 200) {
        Notify("success", "Scholarship Created")
        setSchModal(false)
        fetchPayments()
      } else if (resp?.payload?.status === 208) {
        Notify(
          "warning",
          resp?.payload?.data?.items?.message || "Scholarship already exists.",
        )
      }
    }
  }

  // ---- Edit payment ----
  const openEdit = row => {
    setEditData(pickPayment(row))
    setEditModal(true)
  }

  const handleEditChange = (field, value) => {
    if (field === "isPaid") {
      setEditData(prev => ({ ...prev, isPaid: value === "true" }))
    } else {
      setEditData(prev => ({ ...prev, [field]: value }))
    }
  }

  const handleUpdate = async () => {
    const missing = []
    if (!editData?.currentYear) missing.push("Current Year")
    if (!editData?.currentSemester) missing.push("Current Semester")
    if (!editData?.firstPaymentDueDate) missing.push("Payment Due Date")
    if (
      editData?.paymentModeEnum === "" ||
      editData?.paymentModeEnum === null ||
      editData?.paymentModeEnum === undefined
    )
      missing.push("Payment Mode")
    if (!editData?.amountPaid) missing.push("Amount Paid")
    if (!editData?.paymentMadeDate) missing.push("Payment Made Date")
    if (!editData?.year) missing.push("Year")
    if (!editData?.month) missing.push("Month")
    if (!editData?.challanNumber) missing.push("Challan Number")
    if (missing.length) {
      Notify("error", `Please fill the required field(s): ${missing.join(", ")}`)
      return
    }

    const payload = {
      ...editData,
      isPaid: !!editData.isPaid,
      currentYear: editData.currentYear || null,
      currentSemester: editData.currentSemester || null,
      amountPaid: editData.amountPaid || null,
      paymentModeEnum: editData.paymentModeEnum || null,
      firstPaymentDueDate: editData.firstPaymentDueDate
        ? new Date(editData.firstPaymentDueDate).toISOString()
        : null,
      paymentMadeDate: editData.paymentMadeDate
        ? new Date(editData.paymentMadeDate).toISOString()
        : null,
      year: editData.year || null,
      month: editData.month || null,
      challanNumber: editData.challanNumber || null,
    }

    setSubmitting(true)
    const resp = await dispatch(PaymentUpdate({ id: editData.id, data: payload }))
    setSubmitting(false)

    if (resp?.payload?.status === 200) {
      Notify("success", "Payment Updated")
      setEditModal(false)
      fetchPayments()
    }
  }

  // Only Accepted / Returning applications have a Scholarship Details section.
  if (!canCreate) return null

  return (
    <div className="col-12">
      <h5 className="mt-4 text-secondary-si section-heading page-break">
        Scholarship Details
        {!isViewer && rows.length > 0 && (
          <MdModeEdit
            className="text-primary ms-2"
            size={18}
            style={{ cursor: "pointer", verticalAlign: "middle" }}
            title="Update Scholarship"
            onClick={openScholarshipEdit}
          />
        )}
      </h5>

      {loading ? (
        <Loader />
      ) : rows.length > 0 ? (
        <div className="table-responsive mt-2">
          <Table className="table align-middle mb-0">
            <thead>
              <tr>
                <th className="text-nowrap">Year</th>
                <th className="text-nowrap">Semester</th>
                <th className="text-nowrap">Partner ID</th>
                <th className="text-nowrap">Student Name</th>
                <th className="text-nowrap">Payment Status</th>
                <th className="text-nowrap">Amount Committed</th>
                <th className="text-nowrap">Amount Paid</th>
                <th className="text-nowrap">Challan Number</th>
                <th className="text-nowrap">Modes of Payment</th>
                {!isViewer && <th className="text-nowrap">Action</th>}
              </tr>
            </thead>
            <tbody>
              {rows.map(item => (
                <tr key={item?.id}>
                  <td className="text-nowrap">{item?.currentYear || "-"}</td>
                  <td className="text-nowrap">{item?.currentSemester || "-"}</td>
                  <td className="text-nowrap">{item?.partnerId || "-"}</td>
                  <td className="text-nowrap">{item?.studentName || "-"}</td>
                  <td className="text-nowrap">
                    {item?.isPaid ? (
                      <span className="badge bg-success">Paid</span>
                    ) : (
                      <span className="badge bg-warning">Unpaid</span>
                    )}
                  </td>
                  <td className="text-nowrap">{item?.amountCommitted || "-"}</td>
                  <td className="text-nowrap">{item?.amountPaid || "-"}</td>
                  <td className="text-nowrap">{item?.challanNumber || "-"}</td>
                  <td className="text-nowrap">{item?.paymentModeEnum || "-"}</td>
                  {!isViewer && (
                    <td className="text-nowrap">
                      <MdModeEdit
                        className="text-primary"
                        size={18}
                        style={{ cursor: "pointer" }}
                        onClick={() => openEdit(item)}
                      />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="mt-2">
          <p className="text-muted">
            No scholarship has been created for this application yet.
          </p>
          {!isViewer && canCreate && (
            <button className="btn btn-primary btn-sm" onClick={openCreate}>
              Create Scholarship
            </button>
          )}
        </div>
      )}

      {/* Scholarship create / update modal */}
      <Modal isOpen={schModal} centered size="lg">
        <Card className="bg-white mb-0">
          <CardBody className="mt-2">
            <CardTitle tag="h3" className="text-secondary-si fs-3">
              {schMode === "update" ? "Update Scholarship" : "Create Scholarship"}
            </CardTitle>
            {/* <p className="text-muted">
              Student, country and course duration are taken from this
              application automatically.
            </p> */}
            <div className="row">
              {schMode === "update" && (
                <InputField
                  label="Payment ID"
                  className="col-sm-12 col-md-6 my-2"
                  type="text"
                  readOnly={true}
                  value={schData.paymentId}
                  onChange={() => {}}
                />
              )}
              <InputField
                label="Partner ID"
                compulsory={true}
                className="col-sm-12 col-md-6 my-2"
                type="text"
                value={schData.partnerId}
                placeholder="Enter Partner ID"
                onChange={e => handleSchChange("partnerId", e.target.value)}
              />
              {needCourseDuration && (
                <InputField
                  label="Course Duration"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="text"
                  value={schData.courseDuration}
                  placeholder="Enter Course Duration"
                  onChange={e =>
                    handleSchChange("courseDuration", e.target.value)
                  }
                />
              )}
              <InputNumber
                label="Semesters Per Year"
                compulsory={true}
                className="col-sm-12 col-md-6 my-2"
                type="number"
                min={0}
                value={schData.semestersPerYear}
                placeholder="Enter Semesters Per Year"
                onChange={e =>
                  handleSchChange("semestersPerYear", e.target.value)
                }
              />
              <InputNumber
                label="Annual Scholarship Committed"
                compulsory={true}
                className="col-sm-12 col-md-6 my-2"
                type="number"
                min={0}
                value={schData.annualScholarshipCommitted}
                placeholder="Enter Annual Scholarship Committed"
                onChange={e =>
                  handleSchChange("annualScholarshipCommitted", e.target.value)
                }
              />
              <SelectFieldForId
                label="Currency"
                compulsory={true}
                className="col-sm-12 col-md-6 my-2"
                selectOptions={CURRENCY_OPTIONS}
                value={schData.currency}
                onChange={e => handleSchChange("currency", e.target.value)}
              />
              <InputField
                label="First Payment Due Date"
                compulsory={true}
                className="col-sm-12 col-md-6 my-2"
                type="date"
                value={schData.firstPaymentDueDate}
                placeholder="Select Date"
                onChange={e =>
                  handleSchChange("firstPaymentDueDate", e.target.value)
                }
              />
              <TextareaField
                label="Notes"
                className="col-sm-12 my-2"
                rows="3"
                value={schData.notes}
                placeholder="Enter Notes"
                onChange={e => handleSchChange("notes", e.target.value)}
              />
            </div>
          </CardBody>
          <CardFooter>
            <div className="mt-2 float-end">
              <button
                className="btn btn-primary me-2"
                onClick={handleSchSubmit}
                disabled={submitting}
              >
                {submitting
                  ? schMode === "update"
                    ? "Updating…"
                    : "Creating…"
                  : schMode === "update"
                  ? "Update"
                  : "Create"}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setSchModal(false)}
                disabled={submitting}
              >
                Close
              </button>
            </div>
          </CardFooter>
        </Card>
      </Modal>

      {/* Edit payment modal */}
      <Modal isOpen={editModal} centered size="lg">
        <Card className="bg-white mb-0">
          <CardBody className="mt-2">
            <CardTitle tag="h3" className="text-secondary-si fs-3">
              Update Payment
            </CardTitle>
            {editData && (
              <div className="row">
                <InputField
                  label="Current Year"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="text"
                  value={editData.currentYear}
                  placeholder="Enter Current Year"
                  onChange={e => handleEditChange("currentYear", e.target.value)}
                />
                <InputField
                  label="Current Semester"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="text"
                  value={editData.currentSemester}
                  placeholder="Enter Current Semester"
                  onChange={e =>
                    handleEditChange("currentSemester", e.target.value)
                  }
                />
                <InputField
                  label="Payment Due Date"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="date"
                  value={editData.firstPaymentDueDate}
                  placeholder="Select Date"
                  onChange={e =>
                    handleEditChange("firstPaymentDueDate", e.target.value)
                  }
                />
                <SelectFieldForId
                  label="Is Paid"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  selectOptions={IS_PAID_OPTIONS}
                  value={editData.isPaid}
                  onChange={e => handleEditChange("isPaid", e.target.value)}
                />
                <InputNumber
                  label="Amount Paid"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="number"
                  min={0}
                  value={editData.amountPaid}
                  placeholder="Enter Amount Paid"
                  onChange={e => handleEditChange("amountPaid", e.target.value)}
                />
                <SelectFieldForId
                  label="Payment Mode"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  selectOptions={PAYMENT_MODE_OPTIONS}
                  value={editData.paymentModeEnum}
                  onChange={e =>
                    handleEditChange("paymentModeEnum", e.target.value)
                  }
                />
                <InputField
                  label="Payment Made Date"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="date"
                  value={editData.paymentMadeDate}
                  placeholder="Select Date"
                  onChange={e =>
                    handleEditChange("paymentMadeDate", e.target.value)
                  }
                />
                <SelectFieldForId
                  label="Year"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  selectOptions={YEAR_OPTIONS}
                  value={editData.year}
                  onChange={e => handleEditChange("year", e.target.value)}
                />
                <SelectFieldForId
                  label="Month"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  selectOptions={MONTH_OPTIONS}
                  value={editData.month}
                  onChange={e => handleEditChange("month", e.target.value)}
                />
                <InputField
                  label="Challan Number"
                  compulsory={true}
                  className="col-sm-12 col-md-6 my-2"
                  type="text"
                  value={editData.challanNumber}
                  placeholder="Enter Challan Number"
                  onChange={e =>
                    handleEditChange("challanNumber", e.target.value)
                  }
                />
              </div>
            )}
          </CardBody>
          <CardFooter>
            <div className="mt-2 float-end">
              <button
                className="btn btn-primary me-2"
                onClick={handleUpdate}
                disabled={submitting}
              >
                {submitting ? "Updating…" : "Update"}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => setEditModal(false)}
                disabled={submitting}
              >
                Close
              </button>
            </div>
          </CardFooter>
        </Card>
      </Modal>
    </div>
  )
}
